import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import get from 'lodash/get';
import moment from 'moment';
import { debounce } from 'lodash';

import ExpandableWrapper from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/ExpandableWrapper';
import useExpandable from 'Hooks/useExpandable';

import { selectWsDetail } from 'store/Workspace/selectors';
import { selectWsEditCreateLocale } from 'store/WorkspaceEditCreate/selectors';
import { updateWS, setPendingWorkspaceUpdate } from 'Screens/Contextualization/Workspaces/actions/Actions';

import TopButtons from './TopButtons';
import DateRangePicker from 'Common/Components/Contextualization/DateRangePicket';
import CustomList from 'Common/Components/CustomList';
import { EEFeatureSlot } from 'Common/ee/EEFeatureSlot';

import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_WORKSPACES, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

import { openModal } from 'store/modals/actions';
import { MODAL_WORKSPACE_GROUPING_CONFIRM } from 'store/modals/modals';

import {
  Wrapper, Line, Text, Table, TableBody, Row, Field,
  Label, CreationDate
} from './styled';

const ID = 'technicalInfo';

const DEFAULT_ALG = 'levenshtein';
const DEFAULT_THRESHOLD = 80;

const clampPrecision = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return 10;
  return Math.min(100, Math.max(10, n));
};

const normalizeGroupByValue = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized || normalized === 'none' || normalized === 'null') return 'none';
  if (normalized === 'name' || normalized === 'cve') return normalized;
  return 'none';
};

const TechnicalInfo = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const wsSelected = useSelector(selectWsDetail);
  const locale = useSelector(selectWsEditCreateLocale);

  const wsName = get(wsSelected, 'name', '');
  const publicWs = get(wsSelected, 'public', false);

  const vulnsCount = get(wsSelected, 'stats.total_vulns', 0);
  const assetsCount = get(wsSelected, 'stats.hosts', 0);
  const servicesCount = get(wsSelected, 'stats.services', 0);

  const creationDate = get(wsSelected, 'create_date', 0);
  const start = get(wsSelected, 'duration.start_date', null);
  const end = get(wsSelected, 'duration.end_date', null);

  const scope = get(wsSelected, 'scope', []);

  const title = intl.formatMessage({ id: 'workspace.wsDetail.TechnicalInfo' });

  const canUpdate = useSelector((state) =>
    selectAllowedByRole(state, FEATURE_WORKSPACES, PERMISSION_TYPES.UPDATE)
  );
  const disabled = !canUpdate;

  const isExpanded = useExpandable('general', ID, true, wsSelected?.id);

  const togglePublic = (e) => {
    e.stopPropagation();
    dispatch(updateWS(wsName, { public: !!e.target.checked }));
  };

  const onCloseCalendar = (startDate, endDate) => {
    dispatch(updateWS(wsName, {
      duration: {
        start_date: startDate ? new Date(startDate).getTime() : '',
        end_date: endDate ? new Date(endDate).getTime() : ''
      }
    }));
  };

  const cleanDates = () => {
    dispatch(updateWS(wsName, { duration: { start_date: '', end_date: '' } }));
  };

  const onAddScope = (value) => dispatch(updateWS(wsName, { scope: [...scope, value] }));
  const onRemoveScope = (indexToRemove) => {
    const newScope = scope.filter((_, index) => index !== indexToRemove);
    dispatch(updateWS(wsName, { scope: newScope }));
  };

  const parseDate = (d) => {
    const today = moment();
    const isToday = moment(d).isSame(today, 'day');
    if (isToday) return 'Today';
    return moment(d).format('ddd MMM D YYYY');
  };

  const groupby = get(wsSelected, 'group_by', 'none');
  const matchPrecision = get(wsSelected, 'group_threshold') ?? 80;

  const currentGroupBy = normalizeGroupByValue(wsSelected?.group_by);
  const currentThreshold = wsSelected?.group_threshold ?? 80;
  const currentAlg = wsSelected?.group_algorithm ?? 'levenshtein';

  const requiresRegroup = (next) => {
    const nextGroupBy = Object.prototype.hasOwnProperty.call(next, 'group_by')
      ? normalizeGroupByValue(next.group_by)
      : currentGroupBy;
    const nextAlg = next.group_algorithm ?? currentAlg;
    const nextThreshold = next.group_threshold ?? currentThreshold;

    if (nextGroupBy !== currentGroupBy) return true;
    if (nextAlg !== currentAlg) return true;
    if (nextGroupBy === 'name') {
      if (nextAlg !== currentAlg) return true;
      if (nextThreshold !== currentThreshold) return true;
    }
    return false;
  };

  const debouncedUpdate = useMemo(
    () => debounce((payload) => dispatch(updateWS(wsName, payload)), 350),
    [dispatch, wsName]
  );

  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  const handleMatchPrecisionChange = (next) => {
    if (currentGroupBy !== 'name') return;

    const value = clampPrecision(next);
    const payload = {
      group_by: 'name',
      group_algorithm: currentAlg || DEFAULT_ALG,
      group_threshold: value
    };

    if (wsSelected?.id && requiresRegroup(payload)) {
      dispatch(setPendingWorkspaceUpdate(payload));
      dispatch(openModal(MODAL_WORKSPACE_GROUPING_CONFIRM));
      return;
    }

    debouncedUpdate(payload);
  };

  const buildGroupPayload = (nextCriteria) => {
    const group_by = normalizeGroupByValue(nextCriteria);

    if (group_by === 'name') {
      return {
        group_by,
        group_algorithm: currentAlg || DEFAULT_ALG,
        group_threshold: currentThreshold ?? DEFAULT_THRESHOLD
      };
    }

    if (group_by === 'none') return { group_by: null };

    return { group_by };
  };

  const handleGroupByChange = (nextCriteria) => {
    const payload = buildGroupPayload(nextCriteria);

    if (wsSelected?.id && requiresRegroup(payload)) {
      dispatch(setPendingWorkspaceUpdate(payload));
      dispatch(openModal(MODAL_WORKSPACE_GROUPING_CONFIRM));
      return;
    }

    dispatch(updateWS(wsName, payload));
  };

  return (
    <ExpandableWrapper
      id={ ID }
      title={ title }
      defaultValue={ isExpanded }
      tab="general"
    >
      <Wrapper>
        <TopButtons
          groupby={ groupby }
          matchPrecision={ matchPrecision }
          disabled={ disabled }
          onGroupByChange={ handleGroupByChange }
          onMatchPrecisionChange={ handleMatchPrecisionChange }
        />
        <Table>
          <TableBody>
            <EEFeatureSlot
              featureKey="workspace-public-toggle"
              render={ (PublicToggle) => (
                <PublicToggle publicWs={ publicWs } onToggle={ togglePublic } disabled={ disabled } />
              ) }
              fallback={ null }
            />

            <Line>
              <Text>{ intl.formatMessage({ id: 'workspaces.table.columns.vulnerabilidades' }) }</Text>
              <Text>{ vulnsCount }</Text>
            </Line>
            <Line>
              <Text>{ intl.formatMessage({ id: 'workspaces.table.columns.assets' }) }</Text>
              <Text>{ assetsCount }</Text>
            </Line>
            <Line>
              <Text>{ intl.formatMessage({ id: 'workspaces.table.columns.services' }) }</Text>
              <Text>{ servicesCount }</Text>
            </Line>
          </TableBody>
        </Table>

        <Row>
          <Field>
            <Label>{ intl.formatMessage({ id: 'workspace.wsDetail.creationDate' }) }</Label>
            <CreationDate>{ parseDate(creationDate) }</CreationDate>
          </Field>
          <Field>
            <Label>{ intl.formatMessage({ id: 'workspace.wsDetail.startEndDate' }) }</Label>
            <DateRangePicker
              id="modal-ws-date"
              width="265px"
              height="34px"
              start={ start }
              end={ end }
              onClose={ onCloseCalendar }
              locale={ locale }
              cleanDates={ cleanDates }
              disabled={ disabled }
              hasMargin
            />
          </Field>
        </Row>

        <Field>
          <Label>{ intl.formatMessage({ id: 'workspaces.modal.newWorkspace.scope' }) }</Label>
          <CustomList
            key="MODAL_WS_CREATION"
            placeholder="Scope target"
            addItem={ onAddScope }
            removeItem={ onRemoveScope }
            listData={ scope }
            responsive
            canUpdate={ canUpdate }
          />
        </Field>
      </Wrapper>

    </ExpandableWrapper>
  );
};

export default TechnicalInfo;
